import { useMemo, useState, useEffect, useRef } from "react";
import { Pencil, Plus, Check, X as XIcon, Trash2, Clock } from "lucide-react";

// =====================================================================
// PhaseTabsRow — [ All | Appointment 1 · ₱3,000 ✏️ | … | + Add phase ]
//
// Default label fallback is "Appointment N" (Cameron's mental model is
// phase = appointment). Custom names from plan.phase_names override.
// Tab labels carry per-phase subtotals so the dentist doesn't have to
// switch tabs to see what each phase totals.
//
// Rename mode (pencil → input) shows: ✓ save · 🗑 delete · ✗ cancel.
// Delete cascades clear-assignments + clear-name on the parent.
// =====================================================================
export default function PhaseTabsRow({
  phaseNumbers = [],     // [1, 2, 3]
  phaseNames   = {},     // { "1": "Crown prep", ... }  (custom labels)
  phaseTotals  = {},     // { 1: 300000, 2: 500000 }   (centavos, non-declined)
  phaseChair   = {},     // { 1: 90, 2: 135 }   (chair_min from procedure_visits)
  phaseCalendar = {},    // { 1: { appointment_id, duration_min } } from linked calendar appointments
  unassignedCount = 0,
  unassignedChair = 0,
  activePhase  = "all",
  onChange,
  onRenamePhase,         // (phase_number, name) => Promise
  onSetPhaseAppointmentDuration, // (phase_number, duration_min) => Promise
  onDeletePhase,         // (phase_number) => Promise
  onAddPhase,            // (next_number) => void
  disabled = false,
}) {
  const [editingPhase, setEditingPhase] = useState(null); // phase_number being renamed
  const [draftName, setDraftName] = useState("");
  const [draftDuration, setDraftDuration] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (editingPhase != null) inputRef.current?.focus();
  }, [editingPhase]);

  function startRename(n) {
    setEditingPhase(n);
    setDraftName(phaseNames?.[String(n)] || "");
    setDraftDuration(String(displayMinutes(n, phaseChair, phaseCalendar) || ""));
  }
  async function commitRename() {
    if (editingPhase == null) return;
    const next = draftName.trim();
    const cur  = phaseNames?.[String(editingPhase)] || "";
    try {
      if (next !== cur) await onRenamePhase?.(editingPhase, next);
      const nextDuration = parseDuration(draftDuration);
      const curDuration = displayMinutes(editingPhase, phaseChair, phaseCalendar);
      if (nextDuration != null && nextDuration !== curDuration) {
        await onSetPhaseAppointmentDuration?.(editingPhase, nextDuration);
      }
    } catch {
      return;
    }
    setEditingPhase(null);
    setDraftName("");
    setDraftDuration("");
  }
  function cancelRename() { setEditingPhase(null); setDraftName(""); setDraftDuration(""); }

  async function handleDelete(n) {
    const itemsHere = phaseTotals?.[n] ? "items in this appointment will need placement again. " : "";
    if (!window.confirm(`Delete ${defaultLabel(n, phaseNames)}? ${itemsHere}This can't be undone.`)) return;
    await onDeletePhase?.(n);
    setEditingPhase(null);
    setDraftName("");
    setDraftDuration("");
  }

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="phase-tabs-row">
      <div className="flex items-center gap-1 flex-wrap">
        <Tab active={activePhase === "all"} onClick={() => onChange?.("all")} testid="phase-tab-all">
          All
        </Tab>

        {phaseNumbers.length > 0 ? (
          <Tab
            active={activePhase === "unassigned"}
            onClick={() => onChange?.("unassigned")}
            testid="phase-tab-unassigned"
          >
            <span>Needs appointment</span>
            {unassignedCount > 0 ? (
              <span className={`text-[10px] tabular-nums opacity-70 ${activePhase === "unassigned" ? "" : "text-amber-600"}`}>
                · {unassignedCount}
              </span>
            ) : null}
            {unassignedChair > 0 ? (
              <span className={`text-[10px] tabular-nums opacity-70 ${activePhase === "unassigned" ? "" : "text-slate-400"}`}>
                · {unassignedChair} min
              </span>
            ) : null}
          </Tab>
        ) : null}

        {phaseNumbers.map((n) => {
          const isActive  = activePhase === n;
          const label     = defaultLabel(n, phaseNames);
          const total     = phaseTotals?.[n] ?? 0;
          const isEditing = editingPhase === n;
          const minutes   = displayMinutes(n, phaseChair, phaseCalendar);
          if (isEditing) {
            return (
              <div key={n} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-50 border border-slate-200">
                <input
                  ref={inputRef}
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter")  commitRename();
                    if (e.key === "Escape") cancelRename();
                  }}
                  placeholder={`Appointment ${n}`}
                  maxLength={80}
                  data-testid={`phase-tab-${n}-rename-input`}
                  className="text-xs font-semibold w-32 px-1 py-0.5 rounded border border-slate-200 focus:outline-none focus:border-teal-500"
                />
                <label className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500">
                  <Clock className="w-3 h-3" />
                  <input
                    type="number"
                    inputMode="numeric"
                    value={draftDuration}
                    onChange={(e) => setDraftDuration(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter")  commitRename();
                      if (e.key === "Escape") cancelRename();
                    }}
                    min={5}
                    max={720}
                    step={5}
                    data-testid={`phase-tab-${n}-duration-input`}
                    className="text-xs font-semibold w-16 px-1 py-0.5 rounded border border-slate-200 focus:outline-none focus:border-teal-500 tabular-nums"
                  />
                  min
                </label>
                <button type="button" onClick={commitRename} title="Save"
                        data-testid={`phase-tab-${n}-rename-save`}
                        className="text-emerald-600 hover:text-emerald-700">
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => handleDelete(n)} title="Delete appointment"
                        data-testid={`phase-tab-${n}-delete`}
                        className="text-rose-500 hover:text-rose-700">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={cancelRename} title="Cancel"
                        data-testid={`phase-tab-${n}-rename-cancel`}
                        className="text-slate-400 hover:text-slate-600">
                  <XIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          }
          return (
            <Tab
              key={n}
              active={isActive}
              onClick={() => onChange?.(n)}
              testid={`phase-tab-${n}`}
            >
              <span className="truncate max-w-[160px]">{label}</span>
              {/* Per-phase money chip removed deliberately: billing is
                  plan-level, not per-phase. Showing "Appt 2 · ₱110k"
                  implied "due here" semantics that don't exist — the
                  patient pays against the plan total as they're able.
                  Plan total lives in the footer. */}
              {minutes > 0 ? (
                <span className={`text-[10px] tabular-nums opacity-70 ${isActive ? "" : "text-slate-400"}`}>
                  · {minutes} min
                </span>
              ) : null}
              {!disabled ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); startRename(n); }}
                  data-testid={`phase-tab-${n}-rename`}
                  title="Edit appointment label or blocked time"
                  className={`ml-1 ${isActive ? "text-white/80 hover:text-white" : "text-slate-400 hover:text-slate-600"}`}
                >
                  <Pencil className="w-3 h-3" />
                </button>
              ) : null}
            </Tab>
          );
        })}

        {!disabled ? (
          <button
            type="button"
            onClick={() => {
              const next = phaseNumbers.length === 0 ? 1 : Math.max(...phaseNumbers) + 1;
              onAddPhase?.(next);
            }}
            data-testid="phase-tab-add"
            data-tutorial-id="doctor-tx-plan-add-phase"
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md text-slate-500 border border-dashed border-slate-300 hover:text-teal-700 hover:border-teal-400 hover:bg-teal-50"
          >
            <Plus className="w-3 h-3" /> Add appointment
          </button>
        ) : null}
      </div>

    </div>
  );
}

function defaultLabel(n, phaseNames) {
  return phaseNames?.[String(n)] || `Appointment ${n}`;
}
function displayMinutes(n, phaseChair, phaseCalendar) {
  return Number(phaseCalendar?.[n]?.duration_min ?? phaseChair?.[n] ?? 0) || 0;
}
function parseDuration(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const rounded = Math.round(n);
  return rounded > 0 ? rounded : null;
}
function formatPHP(centavos) {
  return "₱" + Math.round((centavos ?? 0) / 100).toLocaleString("en-PH");
}

function Tab({ active, onClick, testid, children }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.(e);
        }
      }}
      data-testid={testid}
      aria-pressed={active}
      className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
        active ? "bg-slate-900 text-white"
               : "bg-white text-slate-600 border border-slate-200 hover:border-slate-400"
      }`}
    >
      {children}
    </div>
  );
}

// Helper for parents: derive the sorted list of phase numbers from
// (a) items currently assigned to phases and (b) named phases that have
// no items yet. Output is always [1..N] with no gaps below the max.
export function derivePhaseNumbers(items = [], phaseNames = {}) {
  const set = new Set();
  for (const it of items) {
    const n = Number(it?.phase_number);
    if (Number.isFinite(n) && n > 0) set.add(n);
  }
  for (const k of Object.keys(phaseNames || {})) {
    const n = Number(k);
    if (Number.isFinite(n) && n > 0) set.add(n);
  }
  if (set.size === 0) return [];
  const max = Math.max(...set);
  return Array.from({ length: max }, (_, i) => i + 1);
}
